<template>
  <div class="projects-table">
    <AppTabs :items="projectsTabs" :content="false" :default-index="activeProjectTabs" />

    <div v-if="projects" class="base-tale bg-white relative sm:rounded-lg overflow-y-auto">
      <!-- Table filters remain the same -->
      <slot name="table-filters">
        <div v-if="true" class="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4">
          <!-- search input -->
          <div class="w-full md:w-1/2">
            <form class="flex items-center">
              <label for="simple-search" class="sr-only">Search</label>
              <div class="relative w-full">
                <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span class="i-mdi-magnify w-6 h-6 text-slate-500"></span>
                </div>
                <input
                  id="simple-search"
                  type="text"
                  class="bg-slate-100 border border-slate-300 text-sm rounded-lg focus:ring-secondary focus:border-secondary block w-full pl-10 p-2"
                  placeholder="Search projects..."
                  @input="onSearch"
                >
              </div>
            </form>
          </div>
        </div>
      </slot>

      <!-- Table with collapsible rows -->
      <div class="flow-root">
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full py-2 align-middle sm:px-4">
            <table class="min-w-full divide-y divide-slate-300">
                             <thead v-if="!hideHeader">
                 <tr>
                   <th class="py-3.5 text-left text-sm font-semibold w-12"></th> <!-- Expand button column -->
                  <th v-for="(field) in fields" :key="field.key" scope="col" class="py-3.5 text-left text-sm font-semibold">
                    <div class="flex items-center px-2 py-2.5 max-w-max rounded-md">
                      <span>{{ field.label }}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200">
                <tr v-if="isPending">
                  <td :colspan="fields.length + 1">
                    <div class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
                      <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
                      <p class="text-sm text-center text-slate-500 pt-4">Loading...</p>
                    </div>
                  </td>
                </tr>
                <tr v-else-if="error">
                  <td :colspan="fields.length + 1">
                    <div class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
                      <span class="i-heroicons-exclamation-triangle text-red-500 size-8" />
                      <p class="text-sm text-center text-red-600 pt-4 font-medium">Error loading data</p>
                    </div>
                  </td>
                </tr>
                                 <template v-else-if="projects.length > 0">
                   <BaseCollapsibleTableRow
                     :items="projects"
                     :colspan="fields.length + 1"
                   >
                    <template #main-row="{ item: row }">
                      <td
                        v-for="(field, subIndex) in fields"
                        :key="subIndex"
                        :class="[
                          subIndex === 0 && 'pl-4 pr-3 font-medium sm:pl-0',
                          'whitespace-nowrap py-4 text-sm align-top',
                          subIndex !== 0 && 'px-3 text-slate-500'
                        ]"
                      >
                                                 <template v-if="field.key === 'name'">
                           <div class="flex gap-4 justify-between items-center">
                             <div class="project-detail flex gap-2">
                               <ProjectStatusBadge :status="row.status" display="stripe" />
                               <div class="flex flex-col">
                                 <RouterLink :to="{ name: 'ShowCustomer', params: { id: row.customerId } }" class="py-1">
                                   <span class="text-slate-500 font-normal text-xs">{{ customers?.find((customer) => customer.id == row.customerId ).name }}</span>
                                 </RouterLink>
                                 <RouterLink :to="{ name: 'ShowProject', params: { customerId: row.customerId, projectId: row.id } }" class="py-1">
                                   <div class="flex items-center gap-2">
                                     <span class="font-medium">{{ row.name }}</span>
                                     <span class="text-slate-400">•</span>
                                     <div class="text-slate-400">
                                       <ProjectTypeBadge :type="row.type" />
                                     </div>
                                   </div>
                                 </RouterLink>
                                 <ProjectStatusBadge :status="row.status" display="text" />
                               </div>
                             </div>
                             <div class="project-financials flex flex-col items-end gap-2">
                               <!-- Project Total Value -->
                               <div class="text-right">
                                 <div class="text-lg font-bold text-slate-900">
                                   <span v-if="!budgets" class="animate-pulse text-slate-400">...</span>
                                   <span v-else>{{ formatProjectTotal(row.id) }}</span>
                                 </div>
                                 <div class="text-xs text-slate-500">Project Value</div>
                               </div>
                               <!-- Mini Financial Summary -->
                               <div v-if="getProjectFinancialData(row.id) && !isLoadingFinancials(row.id)" class="flex gap-4 text-xs">
                                 <div class="text-right">
                                   <div class="text-green-600 font-medium">{{ currency(getProjectFinancialData(row.id).totalRevenue) }}</div>
                                   <div class="text-slate-500">Revenue</div>
                                 </div>
                                 <div class="text-right">
                                   <div class="text-orange-600 font-medium">{{ currency(getProjectFinancialData(row.id).totalExpenses) }}</div>
                                   <div class="text-slate-500">Expenses</div>
                                 </div>
                                 <div class="text-right">
                                   <div 
                                     :class="[
                                       'font-medium',
                                       (getProjectFinancialData(row.id).totalRevenue - getProjectFinancialData(row.id).totalExpenses) >= 0 ? 'text-green-600' : 'text-red-600'
                                     ]"
                                   >
                                     {{ currency(getProjectFinancialData(row.id).totalRevenue - getProjectFinancialData(row.id).totalExpenses) }}
                                   </div>
                                   <div class="text-slate-500">Profit</div>
                                 </div>
                               </div>
                               <div v-else-if="isLoadingFinancials(row.id)" class="flex gap-4 text-xs">
                                 <div class="text-right">
                                   <div class="animate-pulse text-slate-400">...</div>
                                   <div class="text-slate-500">Revenue</div>
                                 </div>
                                 <div class="text-right">
                                   <div class="animate-pulse text-slate-400">...</div>
                                   <div class="text-slate-500">Expenses</div>
                                 </div>
                                 <div class="text-right">
                                   <div class="animate-pulse text-slate-400">...</div>
                                   <div class="text-slate-500">Profit</div>
                                 </div>
                               </div>
                             </div>
                           </div>
                         </template>
                        <template v-else>
                          {{ row[field.key] }}
                        </template>
                      </td>
                    </template>
                    
                    <template #expandable-content="{ item: row }">
                      <ProjectFinancialOverview
                        :project-id="row.id"
                        :budget="budgets?.[row.id] || null"
                        :financial-data="getProjectFinancialData(row.id)"
                        :is-loading-budget="!budgets"
                        :is-loading-financials="isLoadingFinancials(row.id)"
                      />
                    </template>
                  </BaseCollapsibleTableRow>
                </template>
                
                <tr v-if="(!projects.length || projects.length <= 0) && !isPending && !error">
                  <td :colspan="fields.length + 1">
                    <div class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
                      <span class="i-heroicons-folder-open text-slate-400 size-12" />
                      <p class="text-lg font-medium text-slate-900 mt-4">No projects found</p>
                      <p class="text-sm text-slate-500 mt-2">Get started by creating your first project.</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="projects.length > 0" class="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div class="flex flex-1 justify-between sm:hidden">
          <button
            :disabled="currentPage === 1"
            class="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="prevPage"
          >
            Previous
          </button>
          <button
            :disabled="currentPage === totalPages"
            class="relative ml-3 inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="!isPlaceholderData && nextPage()"
          >
            Next
          </button>
        </div>
        <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p class="text-sm text-slate-700">
              Showing
              <span class="font-medium">{{ ((currentPage - 1) * numPerPage) + 1 }}</span>
              to
              <span class="font-medium">{{ Math.min(currentPage * numPerPage, totalRecords) }}</span>
              of
              <span class="font-medium">{{ totalRecords }}</span>
              results
            </p>
          </div>
          <div>
            <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                :disabled="currentPage === 1"
                class="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="prevPage"
              >
                <span class="sr-only">Previous</span>
                <span class="i-heroicons-chevron-left size-5" />
              </button>
              <span class="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-900 ring-1 ring-inset ring-slate-300 focus:z-20 focus:outline-offset-0">
                {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                :disabled="currentPage === totalPages"
                class="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="!isPlaceholderData && nextPage()"
              >
                <span class="sr-only">Next</span>
                <span class="i-heroicons-chevron-right size-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, Ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import BaseCollapsibleTableRow from '@/components/base/BaseCollapsibleTableRow.vue'
import ProjectFinancialOverview from '@/components/project/ProjectFinancialOverview.vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import BudgetService from '@/services/BudgetService'
import InvoiceService from '@/services/InvoiceService'
import BillService from '@/services/BillService'
import { PROJECT_STATUS } from '@/models/project'
import ProjectStatusBadge from '@/components/project/ProjectStatusBadge.vue'
import ProjectTypeBadge from '@/components/project/ProjectTypeBadge.vue'
import AppTabs, { TabItem } from '@/components/app/AppTabs.vue'
import CustomerService from '@/services/CustomerService'
import { inArray } from '@/utils/queryFilters'
import { useSearchablePagination } from '@/composables/useSearchablePagination'
import { currency } from '@/utils'

const fields = [
  { key: 'name', label: 'Project Details' },
]

const hideHeader = false

const projectsTabs: Ref<TabItem[]> = ref([
  { label: 'Recently Active', to: { name: 'ListProjects', query: { status: 'all'} } },
  { label: 'Budgeting', to: { name: 'ListProjects', query: { status: 'estimating'} } },
  { label: 'Proposal sent', to: { name: 'ListProjects', query: { status: 'awaiting'} } },
  { label: 'In construction', to: { name: 'ListProjects', query: { status: 'inProgress'} } },
  { label: 'Completed invoiced', to: { name: 'ListProjects', query: { status: 'completed'} } },
])

const route = useRoute()
const statusFilterQueryParam = computed(() => route.query.status as string || 'all')
const activeProjectTabs = computed(() => projectsTabs.value.findIndex(tab => (tab.to as any).query.status === statusFilterQueryParam.value))
const projectStatusFilter = computed(() => {
  switch (statusFilterQueryParam.value) {
    case 'estimating':
      return [PROJECT_STATUS.ESTIMATING, PROJECT_STATUS.LEAD]
    case 'awaiting':
      return [PROJECT_STATUS.AWAITING_ACCEPTANCE]
    case 'inProgress':
      return [PROJECT_STATUS.IN_PROGRESS, PROJECT_STATUS.INVOICING]
    case 'completed':
      return [PROJECT_STATUS.COMPLETED, PROJECT_STATUS.ARCHIVED]
    default:
      return [PROJECT_STATUS.ESTIMATING, PROJECT_STATUS.LEAD, PROJECT_STATUS.AWAITING_ACCEPTANCE, PROJECT_STATUS.IN_PROGRESS, PROJECT_STATUS.INVOICING]
  }
})

const queryClient = useQueryClient()
const numPerPage = ref(25)
const projectService = new ProjectService()

const baseQuery = computed(() => [inArray('status', projectStatusFilter.value)])

const {
  results: projectsData,
  isPending,
  error,
  isPlaceholderData,
  currentPage,
  totalPages,
  totalRecords,
  search,
  nextPage,
  prevPage,
} = useSearchablePagination({
  indexName: 'projects',
  service: projectService,
  numPerPage: numPerPage.value,
  defaultOrderBy: 'updatedAt',
  defaultOrderDir: 'desc',
  baseQuery: baseQuery.value
})

const projects = computed(() => {
  if (!projectsData.value) return []

  projectsData.value.forEach(project => {
    queryClient.setQueryData(['project', project.id], project)
  })

  return projectsData.value
})

const uniqueCustomers = computed(() => {
  if (!projects.value) return []
  return projects.value.reduce((acc, project) => {
    if (!acc.includes(project.customerId)) {
      acc.push(project.customerId)
    }
    return acc
  }, [])
})

const customerService = new CustomerService()
const { data: customers } = useQuery({
  queryKey: ['customers', uniqueCustomers, statusFilterQueryParam],
  queryFn: async () => {
    const data = await customerService.where([inArray('id', uniqueCustomers.value)], [])
    return data
  },
  enabled: computed(() => uniqueCustomers.value.length > 0)
})

const budgetService = new BudgetService()

// Fetch budget data for all visible projects to calculate totals
const projectIds = computed(() => {
  if (!projects.value) return []
  return projects.value.map(project => project.id)
})

const { data: budgets } = useQuery({
  queryKey: ['project-budgets', projectIds],
  queryFn: async () => {
    if (!projectIds.value?.length) return {}
    
    const budgetPromises = projectIds.value.map(async (projectId) => {
      try {
        const budget = await budgetService.find(projectId)
        return { projectId, budget }
      } catch (error) {
        console.warn(`Failed to fetch budget for project ${projectId}:`, error)
        return { projectId, budget: null }
      }
    })
    
    const budgetResults = await Promise.all(budgetPromises)
    const budgetMap = {}
    
    budgetResults.forEach(({ projectId, budget }) => {
      budgetMap[projectId] = budget
    })
    
    return budgetMap
  },
  enabled: computed(() => projectIds.value?.length > 0),
  staleTime: 5 * 60 * 1000, // Cache for 5 minutes
})

// Fetch financial data for projects (invoices and bills)
const invoiceService = new InvoiceService()
const billService = new BillService()

const { data: projectFinancials } = useQuery({
  queryKey: ['project-financials', projectIds],
  queryFn: async () => {
    if (!projectIds.value?.length) return {}
    
    const financialPromises = projectIds.value.map(async (projectId) => {
      try {
        const [invoices, bills] = await Promise.all([
          invoiceService.find_by('projectId', '==', projectId),
          billService.find_by('projectId', '==', projectId)
        ])
        
        // Calculate total revenue from invoices
        const totalRevenue = (Array.isArray(invoices) ? invoices : []).reduce((sum, invoice) => {
          const invoiceTotal = Number(invoice.total) || 0
          return sum + invoiceTotal
        }, 0)
        
        // Calculate total expenses from bills
        const totalExpenses = (Array.isArray(bills) ? bills : []).reduce((sum, bill) => {
          const billTotal = Number(bill.total) || 0
          return sum + billTotal
        }, 0)
        
        return {
          projectId,
          financialData: {
            totalRevenue,
            totalExpenses,
            invoices,
            bills
          }
        }
      } catch (error) {
        console.error(`Failed to fetch financials for project ${projectId}:`, error)
        return {
          projectId,
          financialData: {
            totalRevenue: 0,
            totalExpenses: 0,
            invoices: [],
            bills: []
          }
        }
      }
    })
    
    const results = await Promise.all(financialPromises)
    const financialsMap = {}
    
    results.forEach(({ projectId, financialData }) => {
      financialsMap[projectId] = financialData
    })
    
    return financialsMap
  },
  enabled: computed(() => projectIds.value?.length > 0),
  // staleTime: 5 * 60 * 1000, // Cache for 5 minutes
})

// Calculate project total from budget line items
const getProjectTotal = (projectId: string) => {
  if (!budgets.value?.[projectId]) {
    return null
  }
  
  const budget = budgets.value[projectId]
  return budget.getTotalPrice()
}

// Format project total for display
const formatProjectTotal = (projectId: string) => {
  const total = getProjectTotal(projectId)
  return total ? currency(total) : '--'
}

// Get financial data for a specific project
const getProjectFinancialData = (projectId: string) => {
  return projectFinancials.value?.[projectId] || null
}

// Check if financial data is loading for a project
const isLoadingFinancials = (projectId: string) => {
  return !projectFinancials.value || !projectFinancials.value[projectId]
}

// Search functionality
const onSearch = (event: Event) => {
  const target = event.target as HTMLInputElement
  search(target.value)
}
</script>
